import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { PublicMenuTheme } from '../../shared/public-menu-theme';
import { publicThemeCssVariables } from '../../shared/public-menu-theme';

interface PublicMenuThemeContextValue {
  theme: PublicMenuTheme;
  portalContainer: HTMLElement | null;
}

const PublicMenuThemeContext = createContext<PublicMenuThemeContextValue | null>(null);
const loadedFontSheets = new Map<string, { element: HTMLLinkElement; consumers: number }>();
const FONT_READY_TIMEOUT_MS = 2500;
const GOOGLE_FONT_WEIGHTS: Record<string, readonly number[]> = {
  Inter: [400, 500, 600, 700],
  Roboto: [400, 500, 700],
  'Open Sans': [400, 500, 600, 700],
  Montserrat: [400, 500, 600, 700],
  Poppins: [400, 500, 600, 700],
  Nunito: [400, 500, 600, 700],
  Lato: [400, 700],
  Merriweather: [400, 700],
  'Playfair Display': [400, 500, 600, 700],
  Oswald: [400, 500, 600, 700],
  'Bebas Neue': [400],
  'Cabinet Grotesk': [400, 500, 600, 700, 800],
  Cinzel: [400, 500, 600, 700],
  'Cormorant Garamond': [400, 500, 600, 700],
  'DM Sans': [400, 500, 600, 700],
  Lora: [400, 500, 600, 700],
  'Plus Jakarta Sans': [400, 500, 600, 700, 800],
  Quicksand: [400, 500, 600, 700],
  'Space Grotesk': [400, 500, 600, 700],
};

function supportedWeight(family: string, requested: number): number {
  const weights = GOOGLE_FONT_WEIGHTS[family] ?? [400];
  return [...weights].sort((left, right) => Math.abs(left - requested) - Math.abs(right - requested))[0] ?? 400;
}

function googleFontsUrl(theme: PublicMenuTheme): string {
  const requested = new Map<string, Set<number>>();
  const add = (family: string, weight: number) => {
    const weights = requested.get(family) ?? new Set<number>();
    weights.add(weight);
    requested.set(family, weights);
  };
  add(theme.typography.headingFont, supportedWeight(theme.typography.headingFont, theme.typography.headingWeight));
  add(theme.typography.bodyFont, supportedWeight(theme.typography.bodyFont, theme.typography.bodyWeight));
  const families = [...requested]
    .map(([family, weights]) => `family=${family.replaceAll(' ', '+')}:wght@${[...weights].sort().join(';')}`)
    .join('&');
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

function fontLoadDescriptors(theme: PublicMenuTheme): string[] {
  const headingWeight = supportedWeight(theme.typography.headingFont, theme.typography.headingWeight);
  const bodyWeight = supportedWeight(theme.typography.bodyFont, theme.typography.bodyWeight);
  return [
    `${headingWeight} 1em "${theme.typography.headingFont}"`,
    `${bodyWeight} 1em "${theme.typography.bodyFont}"`,
  ];
}

function usePublicMenuFonts(theme: PublicMenuTheme): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    const href = googleFontsUrl(theme);
    const existing = loadedFontSheets.get(href);
    let element: HTMLLinkElement;
    if (existing) {
      existing.consumers += 1;
      element = existing.element;
    } else {
      element = document.createElement('link');
      element.rel = 'stylesheet';
      element.href = href;
      element.dataset.publicMenuFonts = 'true';
      document.head.append(element);
      loadedFontSheets.set(href, { element, consumers: 1 });
    }

    const stylesheetReady = existing || element.sheet
      ? Promise.resolve()
      : new Promise<void>((resolve) => {
        element.addEventListener('load', () => resolve(), { once: true });
        element.addEventListener('error', () => resolve(), { once: true });
      });

    const timeout = new Promise<void>((resolve) => {
      window.setTimeout(resolve, FONT_READY_TIMEOUT_MS);
    });

    void Promise.race([
      (async () => {
        await stylesheetReady;
        const fonts = document.fonts;
        if (!fonts?.load) return;
        await Promise.all(fontLoadDescriptors(theme).map((descriptor) => fonts.load(descriptor)));
      })(),
      timeout,
    ]).finally(() => {
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
      const loaded = loadedFontSheets.get(href);
      if (!loaded) return;
      loaded.consumers -= 1;
      if (loaded.consumers === 0) {
        loaded.element.remove();
        loadedFontSheets.delete(href);
      }
    };
  }, [theme.typography.bodyFont, theme.typography.bodyWeight, theme.typography.headingFont, theme.typography.headingWeight]);

  return ready;
}

export function PublicMenuThemeProvider({
  theme,
  children,
  className = '',
  style: customStyle,
  fontLoadingFallback,
  updateDocumentTheme = false,
  waitForFonts = false,
}: {
  theme: PublicMenuTheme;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  fontLoadingFallback?: ReactNode;
  updateDocumentTheme?: boolean;
  waitForFonts?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const style = useMemo(() => publicThemeCssVariables(theme) as CSSProperties, [theme]);
  const fontsReady = usePublicMenuFonts(theme);

  useEffect(() => {
    setPortalContainer(rootRef.current);
  }, []);

  useEffect(() => {
    if (!updateDocumentTheme) return;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return;
    const previous = meta.content;
    meta.content = theme.colors.background;
    return () => { meta.content = previous; };
  }, [theme.colors.background, updateDocumentTheme]);

  const value = useMemo(() => ({ theme, portalContainer }), [portalContainer, theme]);
  return (
    <PublicMenuThemeContext.Provider value={value}>
      <div
        ref={rootRef}
        className={`public-menu-theme min-h-full ${className}`}
        data-button-style={theme.appearance.buttonStyle}
        data-card-style={theme.appearance.cardStyle}
        data-chat-style={theme.appearance.chatStyle}
        data-density={theme.appearance.density}
        data-navbar-style={theme.appearance.navbarStyle}
        style={{ ...style, ...customStyle }}
      >
        {waitForFonts && !fontsReady ? (
          fontLoadingFallback ?? null
        ) : children}
      </div>
    </PublicMenuThemeContext.Provider>
  );
}

export function usePublicMenuTheme() {
  const value = useContext(PublicMenuThemeContext);
  if (!value) throw new Error('usePublicMenuTheme debe usarse dentro de PublicMenuThemeProvider.');
  return value;
}
